/* eslint-disable camelcase */
import {
    ReactElement,
    useContext,
    useState,
    useEffect,
    SetStateAction,
} from "react";
import { FileUpload } from "../../components/FileUpload/FileUpload";
import { AuthContext, User } from "../../contexts/Auth/AuthContext";
import APIService from "../../services/API";
import "./Profile.scss";
import Textfield from "@atlaskit/textfield";
import TextArea from "@atlaskit/textarea";
import Button from "@atlaskit/button";

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface Props {}

const MAX_AVATAR_SIZE_MB = 1;
const MAX_AVATAR_SIZE_BYTES = MAX_AVATAR_SIZE_MB * 1024 * 1024;

export default function Profile({}: Props): ReactElement {
    const authContext = useContext(AuthContext);
    const [username, setUsername] = useState(authContext.coach?.username);
    const [description, setDescription] = useState(
        authContext.coach?.description,
    );
    const [avatar, setAvatar] = useState<null | string>(
        authContext.coach?.avatar_url as string,
    );
    const [avatarError, setAvatarError] = useState<null | string>(null);
    const [hasChangedUsername, setHasChangedUsername] = useState(false);
    const [hasChangedDescription, setHasChangeDescription] = useState(false);

    useEffect(() => {
        if (authContext.coach) {
            setUsername(authContext.coach.username);
            setAvatar(authContext.coach.avatar_url);
            setDescription(authContext.coach.description);
            setAvatarError(null);
        }
    }, [authContext.coach]);

    const removeAvatar = () => {
        APIService.handlePostRequest<Partial<User>, User>(
            { avatar_url: null },
            "/coach-profile/editCoachProfile",
        ).then((res) => {
            setAvatarError(null);
            authContext.actions.setCoach(res);
        });
    };

    const saveAvatar = (formData: FormData) => {
        APIService.handlePostRequest<FormData, User>(
            formData,
            "/coach-profile/upload",
        ).then((res) => {
            setAvatarError(null);
            authContext.actions.setCoach(res);
        });
    };

    const saveUsername = () => {
        APIService.handlePostRequest<Partial<User>, User>(
            { username },
            "/coach-profile/editCoachProfile",
        ).then((res) => {
            authContext.actions.setCoach(res);
            setHasChangedUsername(false);
        });
    };

    const saveDescription = () => {
        APIService.handlePostRequest<Partial<User>, User>(
            { description },
            "/coach-profile/editCoachProfile",
        ).then((res) => {
            authContext.actions.setCoach(res);
            setHasChangeDescription(false);
        });
    };
    return (
        <div className="form-wrapper">
            <div className="wrapper">
                <div className="elementCont">
                    <div className="profile-username-cont">
                        {" "}
                        <h3>Benutzername</h3>
                        <Textfield
                            value={username || ""}
                            type="text"
                            onChange={(event) => {
                                setHasChangedUsername(true);
                                /*
                                 * Attlaskit has bad types here
                                 */
                                // @ts-expect-error - Atlaskit types problem
                                setUsername(event.target.value);
                            }}
                            placeholder=""
                        />
                        <br />
                        <Button
                            isDisabled={!hasChangedUsername}
                            appearance="primary"
                            onClick={saveUsername}
                        >
                            Benutzername ändern
                        </Button>
                    </div>

                    <div className="profile-avatar-cont">
                        <h3>Avatar</h3>
                        <FileUpload
                            onRemove={removeAvatar}
                            errorMessage={avatarError}
                            onDrop={(event) => {
                                const formData = new FormData();
                                const selectedFile = event.target.files?.[0];

                                if (!selectedFile) {
                                    return;
                                }

                                if (selectedFile.size > MAX_AVATAR_SIZE_BYTES) {
                                    setAvatarError(
                                        `Das Bild ist zu gross. Bitte wähle eine Datei bis maximal ${MAX_AVATAR_SIZE_MB} MB.`,
                                    );
                                    return;
                                }

                                formData.append("avatar", selectedFile);
                                saveAvatar(formData);
                            }}
                            file={avatar}
                        />
                    </div>
                </div>
                <div className="elementCont">
                    <div className="profile-username-cont">
                        <h3>Beschreibung</h3>
                        <TextArea
                            value={description || ""}
                            onChange={(event: {
                                target: {
                                    value: SetStateAction<
                                        string | null | undefined
                                    >;
                                };
                            }) => {
                                setHasChangeDescription(true);
                                setDescription(event.target.value);
                            }}
                            placeholder=""
                        />
                        <br />
                        <Button
                            isDisabled={!hasChangedDescription}
                            appearance="primary"
                            onClick={saveDescription}
                        >
                            Beschreibung ändern
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
